import { Link } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { PageHeader } from "@/components/data-display/page-header";
import { TableState } from "@/components/data-display/table-state";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { WipHint, WIP_ACTION_HINT } from "@/components/ui/wip-hint";

/**
 * Recovery / Key Continuity (`/recovery`) — WIP layout port (ALL-5825).
 *
 * Structure, spacing and tokens are ported from the designer's
 * `.design-ref/prototype/features/recovery/recovery-overview.tsx`
 * (`RecoveryOverview`): title + subtitle → a four-tile KPI row → a two-column
 * Recovery Posture / Shard Distribution band → a Custodians table → the
 * initiate-recovery action → a Recovery History table.
 *
 * Recovery is a custody-adjacent ceremony (key backup / shards / four-eyes
 * approval). It has no backend in this environment, and custody state must
 * never be fabricated, so this is a layout-only port:
 *
 *  - every KPI tile, posture row and table cell that the designer fills from
 *    fixture data renders the shared `UnavailableValue` marker instead, and the
 *    shard lists and both tables render designed empty states;
 *  - the designer's "Healthy" posture badge is omitted: it is a custody status
 *    claim with nothing behind it. Same for the shard counts in the designer's
 *    "HSM Shards (3/5)" headings — the headings stay, the counts do not;
 *  - `Test Recovery` needs the backend (the designer's version only fires a
 *    toast), so it renders disabled and says why through `WIP_ACTION_HINT`;
 *  - `Initiate Recovery Request` is a real link to `/recovery/request`, which
 *    exists in App.tsx (line 318).
 *
 * It replaced a `DeferredPage` (G6, 2026-09-23); the not-available wording
 * survives as the plain sentence under the title. Removing the WIP hint is part
 * of the PR that wires the real API.
 */

const WIP_DESCRIPTION_ID = "recovery-wip-description";

/** The designer's KPI tile, with the shared unavailable marker in the value slot. */
function KpiCard({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-1 text-2xl font-extrabold text-ink">
        <UnavailableValue />
      </div>
    </div>
  );
}

/** The designer's `Row`, with the shared unavailable marker as the value. */
function Row({ k }: { k: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted">{k}</dt>
      <dd className="text-right font-medium text-ink">
        <UnavailableValue />
      </dd>
    </div>
  );
}

/** Panel chrome ported from the designer's `rounded-[10px] border border-line bg-surface`. */
function Panel({
  title,
  children,
  bodyClassName = "p-6",
}: {
  title: string;
  children: React.ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section className="mb-5 overflow-hidden rounded-[10px] border border-line bg-surface">
      <header className="border-b border-line px-6 py-4 text-base font-bold text-ink">{title}</header>
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/** The designer's table shell: panel + horizontal scroll wrapper + header row. */
function TablePanel({
  title,
  columns,
  colSpan,
  emptyTitle,
}: {
  title: string;
  columns: string[];
  colSpan: number;
  emptyTitle: string;
}) {
  return (
    <Panel title={title} bodyClassName="">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-muted">
              {columns.map((column, index) => (
                <th
                  key={column}
                  scope="col"
                  className={index === 0 || index === columns.length - 1 ? "px-6 py-2.5" : "px-3 py-2.5"}
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            <TableState variant="empty" colSpan={colSpan} title={emptyTitle} />
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

export default function RecoveryPage() {
  return (
    <div>
      <PageHeader
        title="Recovery / Key Continuity"
        subtitle="Posture, custodians, and history for the organization's key recovery ceremony."
        actions={<WipHint />}
      />
      <p className="-mt-3 mb-5 text-sm text-muted">
        Recovery has no backend in this environment yet, so nothing here reflects a real recovery request or key state.
      </p>
      <p id={WIP_DESCRIPTION_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </p>

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Key Shards" />
        <KpiCard label="Custodians" />
        <KpiCard label="Active Requests" />
        <KpiCard label="Last Tested" />
      </div>

      <div className="mb-5 grid gap-4 lg:grid-cols-2">
        <Panel title="Recovery Posture">
          <dl className="space-y-2.5 text-sm">
            <Row k="Scheme" />
            <Row k="Key Algorithm" />
            <Row k="Shard Storage" />
            <Row k="Next Required Test" />
            <Row k="Compliance Requirement" />
          </dl>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-4"
            disabled
            title={WIP_ACTION_HINT}
            aria-describedby={WIP_DESCRIPTION_ID}
          >
            <RotateCcw aria-hidden /> Test Recovery
          </Button>
        </Panel>

        <Panel title="Shard Distribution">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">HSM Shards</div>
          <EmptyState
            className="py-6"
            title="No HSM shards"
            description="Hardware shard holdings are not published in this environment yet."
          />
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Human Shards</div>
          <EmptyState
            className="py-6"
            title="No human shards"
            description="Human shard holdings are not published in this environment yet."
          />
        </Panel>
      </div>

      <TablePanel
        title="Custodians"
        columns={["Name", "Role", "Type", "Status", "Last Verified"]}
        colSpan={5}
        emptyTitle="No custodians"
      />

      <div className="mb-5 flex flex-wrap gap-2.5">
        <Link to="/recovery/request" className={buttonVariants({ variant: "primary", size: "md" })}>
          <RotateCcw aria-hidden /> Initiate Recovery Request
        </Link>
      </div>

      <TablePanel
        title="Recovery History"
        columns={["Date", "Type", "Participants", "Duration", "Result", "Ref"]}
        colSpan={6}
        emptyTitle="No recovery history"
      />
    </div>
  );
}
